// Keeping data overlays out of the exported pedigree (TGCLOUD-1670).
//
// There was no export-exclusion mechanism in this codebase at all before this:
// exportImage deep-clones the whole live SVG and strips nothing, and two
// comments in Canvas.jsx celebrate that living inside .diagram means being
// captured by PNG and PDF "with no export-code changes". The agreed position for
// overlays is the opposite, so the mechanism is built here.
//
// Three constraints decide the shape, and each one has a way of being got wrong:
//
//   display, not visibility. Only display:none is excluded from getBBox, which
//   is what frames the export. visibility:hidden is still measured, and writing
//   visibility:'visible' anywhere inside a chart would un-hide an inactive
//   workspace tab, which is hidden exactly that way.
//
//   An inline style, not a class. App.css does not reach an SVG serialised and
//   rendered as an <img>, so a class-based hide would be honoured by the live
//   getBBox and then ignored by the clone.
//
//   Imperative DOM, not React state. Canvas's render effect begins by removing
//   the whole SVG, so a re-render during export would destroy the very element
//   the export is holding a reference to.

import { EXPORT_EXCLUDE_SELECTOR, DIM_ATTR } from './paint';

/**
 * Hide every overlay mark and lift every dim, returning the undo.
 *
 * The caller must run this, read the bounding box, clone, and restore, with no
 * await anywhere in between: an await would let a timer or an image decode
 * repaint into the window.
 */
export function hideDataOverlaysForExport(svgElement) {
  if (!svgElement) return () => {};

  const marks = Array.from(
    svgElement.querySelectorAll(EXPORT_EXCLUDE_SELECTOR)
  );
  const previousDisplay = marks.map((el) => el.style.display);
  marks.forEach((el) => {
    el.style.display = 'none';
  });

  const dimmed = Array.from(svgElement.querySelectorAll(`[${DIM_ATTR}]`));
  const previousOpacity = dimmed.map((el) => el.getAttribute('opacity'));
  dimmed.forEach((el) => el.removeAttribute('opacity'));

  return function restore() {
    marks.forEach((el, i) => {
      // Restoring '' rather than removing the property keeps any inline display
      // the element arrived with, in the manner of selectionHighlight's stash.
      el.style.display = previousDisplay[i] || '';
    });
    dimmed.forEach((el, i) => {
      if (previousOpacity[i] != null)
        el.setAttribute('opacity', previousOpacity[i]);
    });
  };
}

/** Remove the marks from the cloned SVG before it is serialised. */
export function stripDataOverlaysFromClone(clonedSvg) {
  if (!clonedSvg) return;
  clonedSvg
    .querySelectorAll(EXPORT_EXCLUDE_SELECTOR)
    .forEach((el) => el.remove());
  clonedSvg.querySelectorAll(`[${DIM_ATTR}]`).forEach((el) => {
    el.removeAttribute('opacity');
    el.removeAttribute(DIM_ATTR);
  });
}
